import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@comps/ui/card";
import { ChartConfig, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@comps/ui/chart";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { ChartDataItem, getWidgetStyle, RadarChartData, WidgetProps } from "@dto/Dashboard";
import { AggregationType } from "@dto/fields";
import { Period } from "@dto/period";
import { PathsResponse } from "@dto/records";
import { Group, TOP } from "@dto/views";
import { ChartColorType, getColor } from "@utils/colors";
import { getPaths } from "@utils/data";
import { pathKeyToPath } from "@utils/model";
import { toastError } from "@utils/toasts";
import { compareValues, isArray, valueToString } from "@utils/types";
import { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PolarAngleAxis, PolarGrid, Radar, RadarChart } from "recharts";
import { Formatter, NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";
import { translate } from "src/i18n/i18n";
import { useModelStore } from "src/store";
import { hideEmptyCategories } from "./chartCategories";

function isSubYearAggregation(agg: AggregationType): boolean {
    return [AggregationType.DAY, AggregationType.WEEK, AggregationType.MONTH, AggregationType.QUARTER].includes(agg);
}

function getSubYearLabel(period: Period, agg: AggregationType, t: TFunction): string {
    switch (agg) {
        case AggregationType.MONTH: return period.toString(t, "MMM");
        case AggregationType.QUARTER: return period.toString(t, "QQQ");
        case AggregationType.WEEK: return period.toString(t, "WWW");
        default: return period.toString(t, "dd/MM");
    }
}

function getSubYearSortKey(period: Period, agg: AggregationType): number {
    switch (agg) {
        case AggregationType.MONTH: return period.month();
        case AggregationType.QUARTER: return period.quarter();
        case AggregationType.WEEK: return period.week();
        default: return period.day();
    }
}

export function RadarChartCard(props: WidgetProps) {
    const widget = props.widget.data as RadarChartData;

    const objectInfos = useModelStore(state => state.objectInfos);
    const workspace = useModelStore(state => state.selectedWorkspace);
    const relationByKeys = useModelStore(state => state.relationByKeys);
    const fieldByKeys = useModelStore(state => state.fieldByKeys);
    const [chartData, setChartData] = useState<ChartDataItem[]>([]);
    const [chartConfig, setChartConfig] = useState<ChartConfig>({});
    const [categories, setCategories] = useState<string[]>([]);
    const { t } = useTranslation();
    const valuePathKey = widget.valuePath;
    const valueAggregation = widget.valueAggregation ?? AggregationType.NO_AGGR;
    const splitByPathKey = widget.splitByPath;
    const splitByAggregation = widget.splitByAggregation ?? AggregationType.NO_AGGR;
    const splitBySortType = widget.splitBySortType;
    const categoryPathKey = widget.categoryPath;
    const categoryAggregation = widget.categoryAggregation ?? AggregationType.NO_AGGR;
    const categorySortType = widget.categorySortType;
    const categoryByYear = widget.categoryByYear ?? false;
    const showEmptyCategories = widget.showEmptyCategories ?? false;
    const filter = widget.filter;

    const valuePath = pathKeyToPath(valuePathKey, relationByKeys);
    const valueField = fieldByKeys?.get(valuePath?.field ?? "");
    const splitByPath = pathKeyToPath(splitByPathKey, relationByKeys);
    const splitByField = fieldByKeys?.get(splitByPath?.field ?? "");
    const categoryPath = pathKeyToPath(categoryPathKey, relationByKeys);
    const categoryField = fieldByKeys?.get(categoryPath?.field ?? "");

    const aggregationMap = new Map<string, AggregationType>();
    if (valuePathKey && valueAggregation) {
        aggregationMap.set(valuePathKey, valueAggregation);
    }
    if (splitByPathKey && splitByAggregation) {
        aggregationMap.set(splitByPathKey, splitByAggregation);
    }
    if (categoryPathKey && categoryAggregation) {
        aggregationMap.set(categoryPathKey, categoryAggregation);
    }
    const categoryFormat = categoryField?.format;
    
    useEffect(() => {
        if (!workspace) {
            return;
        }
        const srcObjectKey = valuePath?.srcObject ?? valuePath?.object;
        if (!valuePath || !splitByPath || !srcObjectKey) {
            return;
        }

        if (!categoryPath) {
            const sortings = [{ pathKey: splitByPathKey!, sort: splitBySortType }];
            const groups: Group[] = [{ pathKey: splitByPathKey!, sort: splitBySortType, hideEmptyGroups: false, displayType: TOP, applyGroupFilter: false, filter: null }];
            getPaths(workspace._key, srcObjectKey, [valuePath, splitByPath], null, filter, sortings, groups, null, null, aggregationMap).then(response => {
                if (response) {
                    if (isCompozzError(response)) {
                        const cError = response as CompozzError;
                        if (cError.i18n) {
                            toastError(translate(t, cError.i18n, cError.params));
                        } else {
                            toastError(cError.message);
                        }
                    } else {
                        const pathsResponse = response as PathsResponse;
                        if (pathsResponse.lines && pathsResponse.lines.length > 0) {
                            const chartData: any[] = []
                            const chartConfig: ChartConfig = {}
                            const categories: string[] = []
                            const valuesBySplitBy: Map<string, Map<string, number>> = new Map();

                            if (categoryByYear && isSubYearAggregation(splitByAggregation)) {
                                const sortKeyBySplitBy: Map<string, number> = new Map();
                                const yearsSet: Set<string> = new Set();

                                for (const group of pathsResponse.lines) {
                                    if (group.children) {
                                        for (const line of group.children) {
                                            let rawSplitBy = line.items[1].value ?? "";
                                            if (isArray(rawSplitBy)) rawSplitBy = rawSplitBy[0];
                                            const period = Period.fromObject(rawSplitBy);
                                            if (!period) {
                                                continue;
                                            }
                                            const yearLabel = period.year().toString();
                                            const subKey = getSubYearLabel(period, splitByAggregation, t);
                                            const sortKey = getSubYearSortKey(period, splitByAggregation);
                                            const value = line.items[0].value ?? 0;
                                            const values = valuesBySplitBy.get(subKey) ?? new Map<string, number>();
                                            values.set(yearLabel, value);
                                            valuesBySplitBy.set(subKey, values);
                                            sortKeyBySplitBy.set(subKey, sortKey);
                                            yearsSet.add(yearLabel);
                                        }
                                    }
                                }

                                const sortedYears = Array.from(yearsSet).sort();
                                sortedYears.forEach((year, idx) => {
                                    chartConfig[year] = { label: year, color: getColor(ChartColorType, idx) };
                                    categories.push(year);
                                });

                                valuesBySplitBy.forEach((values, subKey) => {
                                    const chartDataItem: any = { splitBy: subKey, _sortKey: sortKeyBySplitBy.get(subKey) ?? 0 };
                                    sortedYears.forEach(year => {
                                        chartDataItem[year] = values.get(year) ?? 0;
                                    });
                                    chartData.push(chartDataItem);
                                });

                                chartData.sort((a, b) => a._sortKey - b._sortKey);
                            } else {
                                const category = "Category";
                                for (const group of pathsResponse.lines) {
                                    if (group.children) {
                                        for (const line of group.children) {
                                            let splitBy = line.items[1].value ?? "";
                                            if (isArray(splitBy)) {
                                                splitBy = splitBy[0];
                                            }
                                            let splitByKey = valueToString(splitBy, splitByField!, splitByAggregation, undefined, t, "", fieldByKeys);
                                            if (!splitByKey) {
                                                splitByKey = t("dashboard.label.empty");
                                            }
                                            const value = line.items[0].value ?? 0;
                                            const values = valuesBySplitBy.get(splitByKey) ?? new Map<string, number>();
                                            values.set(category, value);
                                            valuesBySplitBy.set(splitByKey, values);
                                        }
                                    }
                                }
                                const barColor = getColor(ChartColorType, 0);
                                chartConfig[category] = { label: category, color: barColor };
                                categories.push(category);

                                valuesBySplitBy.forEach((values, splitByKey) => {
                                    const chartDataItem: any = { splitBy: splitByKey };
                                    values.forEach((value, category) => { chartDataItem[category] = value; });
                                    categories.forEach((category) => {
                                        if (!chartDataItem[category]) chartDataItem[category] = 0;
                                    });
                                    chartData.push(chartDataItem);
                                });

                                chartData.sort((a, b) => {
                                    return compareValues(a.splitBy, b.splitBy, splitByField!, splitByAggregation, splitBySortType, t, fieldByKeys);
                                });
                            }

                            setChartData(chartData);
                            setChartConfig(chartConfig);
                            setCategories(categories);
                        } else {
                            // setValues(new Map());
                            setChartData([]);
                            setChartConfig({});
                            setCategories([]);
                        }
                    }
                }
            })
        } else {
            const sortings = [{ pathKey: splitByPathKey!, sort: splitBySortType }];
            const hideEmptyGroups = hideEmptyCategories(showEmptyCategories, filter, categoryPathKey!);
            const groups: Group[] = [{ pathKey: categoryPathKey!, sort: categorySortType, hideEmptyGroups: hideEmptyGroups, displayType: TOP, applyGroupFilter: false, filter: null }];
            getPaths(workspace._key, srcObjectKey, [valuePath, splitByPath, categoryPath], null, filter, sortings, groups, null, null, aggregationMap).then(response => {
                if (response) {
                    if (isCompozzError(response)) {
                        const cError = response as CompozzError;
                        if (cError.i18n) {
                            toastError(translate(t, cError.i18n, cError.params));
                        } else {
                            toastError(cError.message);
                        }
                    } else {
                        const pathsResponse = response as PathsResponse;
                        if (pathsResponse.lines && pathsResponse.lines.length > 0) {
                            const chartData: any[] = []
                            const chartConfig: ChartConfig = {}
                            const categories: string[] = []
                            let index = 0;
                            const valuesBySplitBy: Map<string, Map<string, number>> = new Map();
                            for (const group of pathsResponse.lines) {
                                let category = group.items[0].value ?? "";
                                if (isArray(category)) {
                                    category = valueToString(category[0], categoryField!, categoryAggregation, categoryFormat, t, "", fieldByKeys);
                                } else {
                                    category = valueToString(category, categoryField!, categoryAggregation, categoryFormat, t, "", fieldByKeys);
                                }
                                if (!category) {
                                    category = t("dashboard.label.empty");
                                }
                                const barColor = getColor(ChartColorType, index);
                                if (group.children) {
                                    for (const line of group.children) {
                                        let splitBy = line.items[1].value ?? "";
                                        if (isArray(splitBy)) {
                                            splitBy = splitBy[0];
                                        }
                                        let splitByKey = valueToString(splitBy, splitByField!, splitByAggregation, undefined, t, "", fieldByKeys);
                                        if (!splitByKey) {
                                            splitByKey = t("dashboard.label.empty");
                                        }
                                        const value = line.items[0].value ?? 0;
                                        const values = valuesBySplitBy.get(splitByKey) ?? new Map<string, number>();
                                        values.set(category, value);
                                        valuesBySplitBy.set(splitByKey, values);
                                    }
                                }
                                chartConfig[category] = {
                                    label: category,
                                    color: barColor,
                                }
                                categories.push(category);
                                index++;
                            }

                            valuesBySplitBy.forEach((values, splitByKey) => {
                                const chartDataItem: any = {
                                    splitBy: splitByKey,
                                }

                                values.forEach((value, category) => {
                                    chartDataItem[category] = value;
                                })
                                categories.forEach((category) => {
                                    if (!chartDataItem[category]) {
                                        chartDataItem[category] = 0;
                                    }
                                })
                                chartData.push(chartDataItem);
                            })

                            chartData.sort((a, b) => {
                                return compareValues(a.splitBy, b.splitBy, splitByField!, splitByAggregation, splitBySortType, t, fieldByKeys);
                            });

                            categories.sort((a, b) => {
                                return compareValues(a, b, categoryField!, categoryAggregation, categorySortType, t, fieldByKeys);
                            });

                            setChartData(chartData);
                            setChartConfig(chartConfig);
                            setCategories(categories);
                        } else {
                            // setValues(new Map());
                            setChartData([]);
                            setChartConfig({});
                            setCategories([]);
                        }
                    }
                }
            })
        }
    }, [objectInfos, fieldByKeys, widget, filter, t]);

    const srcObjectKey = valuePath?.srcObject ?? valuePath?.object;
    if (!valuePathKey || !splitByPathKey || !srcObjectKey) {
        return <div>{t("dashboard.overview.not.configured")}</div>;
    }

    if (!workspace) {
        return <div>{t("dashboard.overview.not.configured")}</div>;
    }

    if (chartData.length === 0) {
        return <div>{t("dashboard.overview.no.data")}</div>;
    }

    // let xAxisDataKey = "splitBy";
    // let xAxisIsPeriod = false;
    // if (splitByField && fieldToOutputType(splitByField, splitByAggregation) === FieldType.Period) {
    //     xAxisDataKey = "splitBy.date";
    //     xAxisIsPeriod = true;
    // }
    const style = getWidgetStyle(props.widget);

    let formatter: Formatter<ValueType, NameType> | undefined = undefined;
    const valueFormat = valueField?.format;
    const valueFormatter = (value: ValueType) => valueToString(value, valueField!, valueAggregation, valueFormat, t, "", fieldByKeys);
    if (!categoryPath) {
        formatter = (value: any) => {
            if (value) {
                return "valeur : " + valueFormatter(value);
            }
            return "";
        }
    }
    return (
        <Card>
            {props.widget.title &&
                <CardHeader className="items-center">
                    <CardTitle>{props.widget.title}</CardTitle>
                    {props.widget.description &&
                        <CardDescription>
                            {props.widget.description}
                        </CardDescription>
                    }
                </CardHeader>
            }
            <CardContent className="pb-0">
                <ChartContainer
                    config={chartConfig}
                    className="mx-auto aspect-square" style={style}>
                    <RadarChart data={chartData}>
                        <ChartTooltip cursor={false} 
                            content={<ChartTooltipContent hideIndicator={categoryPath ? false : true} formatter={formatter} valueFormatter={valueFormatter} />} />
                        <PolarAngleAxis dataKey="splitBy" />
                        <PolarGrid />
                        {categories.map((category) => (
                            <Radar
                                key={category}
                                dataKey={category}
                                fill={chartConfig[category]?.color ?? ""}
                                fillOpacity={0.5}
                                dot={{
                                    r: 3,
                                    fillOpacity: 0.8,
                                }}
                            />
                        ))}
                        {props.widget.showLegend && (categoryPath || categoryByYear) && <ChartLegend content={<ChartLegendContent className="flex-wrap" />} />}
                    </RadarChart>
                </ChartContainer>
            </CardContent>
        </Card>
    );
}